import { Injectable } from '@angular/core';
import { User } from '@core/models';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';
import { getAngularJSService } from '../ng-upgrade.utils';

interface LegacyRootScope {
  eventLoading?: boolean;
  loggedUser?: User | null;
  isAccessAllowed?: boolean;
  $watch?(expression: string, listener: (value: unknown) => void): () => void;
  $applyAsync?(fn: () => void): void;
}

@Injectable({ providedIn: 'root' })
export class AppService {
  private readonly eventLoading$ = new BehaviorSubject<boolean>(false);
  private readonly loading$ = this.eventLoading$.pipe(distinctUntilChanged());
  private unwatchRootScope: (() => void) | null = null;
  private rootScopeRef: LegacyRootScope | null = null;

  get isLoading$(): Observable<boolean> {
    return this.loading$;
  }

  setLoading(value: boolean): void {
    const loading = value === true;
    this.eventLoading$.next(loading);
    this.patchRootScope((rootScope) => {
      rootScope.eventLoading = loading;
    });
  }

  setLoggedUser(user: User | null): void {
    this.patchRootScope((rootScope) => {
      rootScope.loggedUser = user;
    });
  }

  setAccessAllowed(allowed: boolean): void {
    this.patchRootScope((rootScope) => {
      rootScope.isAccessAllowed = allowed;
    });
  }

  syncWithRootScope(): void {
    if (this.unwatchRootScope) {
      return;
    }
    const rootScope = this.rootScope;
    if (!rootScope || typeof rootScope.$watch !== 'function') {
      console.error('[AppService] $rootScope indisponível — eventLoading não será sincronizado.');
      return;
    }
    this.unwatchRootScope = rootScope.$watch('eventLoading', (value: unknown) => {
      this.eventLoading$.next(value === true);
    });
  }

  private get rootScope(): LegacyRootScope | null {
    if (this.rootScopeRef) {
      return this.rootScopeRef;
    }
    this.rootScopeRef = getAngularJSService<LegacyRootScope>('$rootScope');
    return this.rootScopeRef;
  }

  private patchRootScope(mutate: (rootScope: LegacyRootScope) => void): void {
    const rootScope = this.rootScope;
    if (!rootScope) {
      return;
    }

    if (typeof rootScope.$applyAsync === 'function') {
      rootScope.$applyAsync(() => mutate(rootScope));
      return;
    }
    mutate(rootScope);
  }
}
